import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cx from 'classnames';
import { pointType } from '@moleculeone/utils';
import { getBoundingPoints } from '@moleculeone/draw';
import { SVGMol } from '../Compound/SVGMol';
import { SVGMolSpace } from '../Compound/SVGMolSpace';
import { theme } from '../../../stitches.config';

import MoleculeBackground from './components/MoleculeBackground';

import {
  getAllSurvivingAtomsColorMapping,
  restrictToSurvivingSubstrates,
  applyColorMapping,
} from './utils/colorMapping';
import { HIGHLIGHTING_MODES } from './utils/highlighting';

const COLORS = ['orange', 'green', 'brown', 'blue', 'purple', 'teal', 'pink', 'tomato'].map(el => {
  return theme.colors[`${el}10`].value;
});

const getColorMapping = (mode, nodes, globalMapping) => {
  let result;
  switch (mode) {
    case HIGHLIGHTING_MODES.SURVIVING_SUBSTRATE_ATOMS:
      result = getAllSurvivingAtomsColorMapping(nodes, globalMapping, COLORS);
      return restrictToSurvivingSubstrates(result, globalMapping, 0);
    case HIGHLIGHTING_MODES.ALL_SUBSTRATE_ATOMS:
      return getAllSurvivingAtomsColorMapping(nodes, globalMapping, COLORS);
    default:
      return nodes;
  }
};

const contextualPointType = PropTypes.shape({
  point: pointType,
  context: PropTypes.shape({
    node: PropTypes.node,
    props: PropTypes.object,
  }),
});

export class SynthesisTreeSVG extends PureComponent {
  static propTypes = {
    nodes: PropTypes.object.isRequired,
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        from: PropTypes.number,
        to: PropTypes.number,
        prediction: PropTypes.number,
        data: PropTypes.arrayOf(contextualPointType),
        label: contextualPointType,
      }),
    ).isRequired,
    highlightedAtomUuid: PropTypes.string,
    highlightedMoleculeId: PropTypes.number,
    mapping: PropTypes.object,
    highlightingMode: PropTypes.oneOf(Object.values(HIGHLIGHTING_MODES)),
    debugEnabled: PropTypes.bool,
    renderMoleculeContext: PropTypes.func,
    onlyCopySmilesOptionVisible: PropTypes.bool,
    scale: PropTypes.number,
    className: PropTypes.string,
    id: PropTypes.string,
  };

  state = {
    highlightingMode: HIGHLIGHTING_MODES.SURVIVING_SUBSTRATE_ATOMS,
    forceSelectedMoleculeId: null,
    recentlyChangedNodes: [],
  };

  componentDidMount() {
    const { highlightingMode } = this.props;

    this.setState({
      highlightingMode,
    });
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.scale, this.props.scale)) {
      const recentlyChangedNodes = _.difference(Object.keys(this.props.nodes), Object.keys(prevProps.nodes));
      if (recentlyChangedNodes.length > 0) {
        this.setState({
          recentlyChangedNodes,
        });
      }
    }
  }

  highlightAtom = (id, index, mol, highlightedMoleculeId, highlightedAtomUuid, mapping) => {
    if (!highlightedAtomUuid) return mol;
    const highlightedAtomIndexes = Object.values(mapping).reduce((acc, curr) => {
      return [...acc, curr.findIndex(atomUuid => atomUuid === highlightedAtomUuid)];
    }, []);
    const atomIndex = highlightedAtomIndexes[index];
    const target = Object.values(mol.atoms).find(atom => Number(atom.idx) === atomIndex);
    if (target) {
      // TODO: more real highlighting
      target.color = 'orange';
    }
    return mol;
  };

  onPopoverOpen = id => {
    this.setState({
      forceSelectedMoleculeId: id,
    });
  };

  onPopoverClose = () => {
    this.setState({
      forceSelectedMoleculeId: null,
    });
  };

  render() {
    const {
      nodes,
      edges,
      highlightedAtomUuid,
      highlightedMoleculeId,
      mapping,
      debugEnabled,
      renderMoleculeContext,
      onlyCopySmilesOptionVisible,
      scale,
      className,
      id,
    } = this.props;

    const { highlightingMode, forceSelectedMoleculeId, recentlyChangedNodes } = this.state;

    if (!Object.keys(nodes).length || !mapping) {
      return null;
    }

    const bpts = getBoundingPoints(Object.values(nodes).map(node => node.drawnMol));

    const compoundColorMapping = getColorMapping(highlightingMode, nodes, mapping);

    const MoleculeContext = renderMoleculeContext;

    const components = Object.values(nodes).map((node, i) => {
      const drawnMol = _.cloneDeep(node.drawnMol);

      let drawnMolWithHighlights;
      switch (highlightingMode) {
        case HIGHLIGHTING_MODES.SINGLE_ATOM:
          drawnMolWithHighlights = this.highlightAtom(
            node.id,
            i,
            drawnMol,
            highlightedMoleculeId,
            highlightedAtomUuid,
            mapping,
          );
          break;
        case HIGHLIGHTING_MODES.ALL_SUBSTRATE_ATOMS:
          drawnMolWithHighlights = applyColorMapping(node.id, drawnMol, compoundColorMapping, mapping);
          break;
        case HIGHLIGHTING_MODES.SURVIVING_SUBSTRATE_ATOMS:
          drawnMolWithHighlights = applyColorMapping(node.id, drawnMol, compoundColorMapping, mapping);
          break;
        default:
          drawnMolWithHighlights = drawnMol;
      }

      return (
        <SVGMol
          scale={scale}
          className={cx('molecule-container', {
            debug: debugEnabled,
          })}
          data-molecule-id={node.id}
          idx={`tree-${id}_node-${node.id}`}
          drawnMol={drawnMolWithHighlights}
          before={
            <MoleculeBackground
              data-molecule-idx={i}
              data-molecule-id={node.id}
              bbox={drawnMolWithHighlights.bbox}
              scale={scale}
              selected={forceSelectedMoleculeId === node.id || highlightedMoleculeId === node.id}
              highlightChanges={recentlyChangedNodes.includes(String(node.id))}
            />
          }
          after={
            MoleculeContext && (
              <MoleculeContext
                bbox={drawnMolWithHighlights.bbox}
                scale={scale}
                onOpen={this.onPopoverOpen}
                onClose={this.onPopoverClose}
                onCopySmiles={this.props.onCopySmiles}
                getSmilesById={this.props.getSmilesById}
                onRedraw={this.props.onRedraw}
                selected={forceSelectedMoleculeId === node.id || highlightedMoleculeId === node.id}
                moleculeId={node.id}
                onlyCopySmilesOptionVisible={onlyCopySmilesOptionVisible}
              />
            )
          }
          key={`molecule-${node.id}`}
          data-test={`svg-space-${node.id}`}
        />
      );
    });

    const contextObjects = [];
    const debugInfo = [];

    const connections = edges.map((edge, index) => {
      const edgeData = edge.data;
      const { from, to, debug } = edge;
      const isHighlighted = to === highlightedMoleculeId || from === highlightedMoleculeId;

      const polySVGPoints = edgeData.reduce((acc, { point }) => {
        const [x, y] = point;
        return `${acc} ${x},${y}`;
      }, '');

      const createDebugLabel = data => {
        const { point, renderPointContext } = data;
        const padding = 0;

        if (!renderPointContext) {
          return null;
        }

        return <Fragment key={`debug-label-${index}`}>{renderPointContext(point, scale, padding)}</Fragment>;
      };
      debugInfo.push(createDebugLabel(debug));

      contextObjects.push(
        ...edgeData
          .filter(data => Boolean(data.renderPointContext))
          .map((data, i) => {
            const { point, renderPointContext } = data;
            const padding = 0;

            return <Fragment key={`context-${index}-${i}`}>{renderPointContext(point, scale, padding)}</Fragment>;
          }),
      );

      return (
        <g key={`edge-${index}`}>
          <polyline
            points={polySVGPoints}
            fill="none"
            stroke={isHighlighted ? '#8e4ec6' : '#5c7080'}
            strokeWidth={isHighlighted ? '10' : '4'}
          />
        </g>
      );
    });

    return (
      <SVGMolSpace className={cx(className, 'mol')} viewport={bpts} padding={60}>
        <defs>
          <marker
            id="arrowhead"
            viewBox="0 0 10 10"
            markerWidth="10"
            markerHeight="10"
            refX="1"
            refY="5"
            markerUnits="strokeWidth"
          >
            <path d="M 10 0 L 0 5 L 10 10 z" fill="#8e4ec6" />
          </marker>
        </defs>

        {components}
        {connections}
        {contextObjects}
        {debugEnabled && debugInfo}
      </SVGMolSpace>
    );
  }
}
